import React, {FC, ReactElement, useEffect, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import queryString from 'query-string'
import moment from 'moment'
import {last} from 'lodash'
import {reduce} from 'lodash/fp'
import {useSelector} from 'hooks/redux'
import {useHasBranchOffice} from 'hooks/useHasBranchOffice'
import {Close} from 'jbc-front/components/icons'
import {CsvDownloadModal} from 'pages/withAuth/adminPage/procedureStatuses/index/parts/CsvDownloadModal'
import {ProcedureStatus, Status} from 'types/api/procedureStatuses/procedureStatus'
import {ProcedureType} from 'types/api/procedureStatuses/procedureType'
import {ProcedureKind as csvProcedureKind} from 'types/api/csvProcedureStatuses/procedureKind'
import {PROCEDURE_NAMES} from 'consts/egovProcedures'
import {StarFlagColumn} from './parts/StarFlagColumn'
import {EgovPrepareModal} from './parts/EgovPrepareModal'
import {PrepareCsvProcedureAsyncStatus} from './parts/PrepareCsvProcedureAsyncStatus'
import {ProcedureKind as egovProcedureKind, PrepareCsvProcedureRequest} from './query'
import styles from './Presenter.scss'

// @ts-ignore
import {recordDisplay} from 'utils'
// @ts-ignore
import SelectList from 'components/SelectList'
// @ts-ignore
import {Pager, PerPage, CsvListData, Search, OrderedTh} from 'procedureStatuses/list/components'
// @ts-ignore
import {procedureTypes, statuses, dateNameMap} from 'procedureStatuses/List'

type RowProps = {
  procedureStatus: ProcedureStatus
  td: ReactElement
  hasBranchOffice: boolean
}
type ListProps = {
  item: ProcedureStatus
  selected: boolean
  td: ReactElement
  toggle: () => void
}

type Item = {
  name: string
}

type SelectListProps = {
  list: ListProps[]
  selected: {[key: number]: Item}
  th: ReactElement
}

type ParseQuery = Partial<{
  procedure_kind: csvProcedureKind | egovProcedureKind
  status: Status
  procedure_type: ProcedureType
  action_type: 'export_csv' | 'egov'
  office_ids: string[]
}>

type ValidationErrorsProps = {
  validationErrors: string[]
}

const ValidationErrors: FC<ValidationErrorsProps> = ({validationErrors}) => {
  const [visible, setVisible] = useState(true)
  useEffect(() => {
    setVisible(true)
  }, [validationErrors])

  if (!(visible && validationErrors?.length > 0)) return <></>

  return (
    <div className={styles.warning}>
      <div className={styles.warningBox}>
        <div className={styles.warningBoxText}>
          以下のエラーが発生しています
          <Close className={styles.closeButton} onClick={() => setVisible(false)} />
        </div>
        {validationErrors.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
      </div>
    </div>
  )
}

type PollingControllerProps = {
  status: string
  startPolling: (pollInterval: number) => void
  stopPolling: () => void
}
const PollingController: FC<PollingControllerProps> = ({status, startPolling, stopPolling}) => {
  useEffect(() => {
    if (status !== 'success' && status !== 'failed') {
      startPolling(3000)
    }

    return () => {
      stopPolling()
    }
  })
  return null
}

const getRunningRequest = (requests: PrepareCsvProcedureRequest[]) =>
  requests.find((request) => ['waiting', 'in_progress'].includes(request.status))

type PresenterProps = {
  prepareCsvProcedureRequests: PrepareCsvProcedureRequest[]
  refetch: () => void
  startPolling: (pollInterval: number) => void
  stopPolling: () => void
}

export const Presenter: FC<PresenterProps> = ({prepareCsvProcedureRequests, refetch, startPolling, stopPolling}) => {
  const history = useHistory()
  const {search} = useLocation()
  const loading = useSelector((state) => state.procedureStatuses.list.loading)
  const procedureStatuses = useSelector((state) => state.procedureStatuses.list.data)
  const count = useSelector((state) => state.procedureStatuses.list.count)
  const currentUser = useSelector((state) => state.session.currentUser)
  const hasBranchOffice = useHasBranchOffice()
  const {procedure_kind, status, procedure_type, action_type, office_ids = []}: ParseQuery = queryString.parse(search)
  const MAX_COUNT = 999
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const runningRequest = getRunningRequest(prepareCsvProcedureRequests)
  const isActedUser = runningRequest?.actedUserId === String(currentUser.id)
  const prepareRequest = last(
    prepareCsvProcedureRequests.filter((request) => request.actedUserId === String(currentUser.id))
  )
  const isExportCsv = action_type === 'export_csv'
  const isEgovPrepare = action_type === 'egov'
  const label = isExportCsv ? '書類' : '電子申請'
  const officeIds = Array.isArray(office_ids) ? office_ids : [office_ids]

  if (!procedure_kind || !status || !procedure_type || !action_type || (hasBranchOffice && officeIds.length === 0)) {
    history.push('/procedure_statuses')
    return null
  }

  return (
    <SelectList
      list={procedureStatuses}
      maxCount={MAX_COUNT}
      overMaxCountMessage={`選択できる手続きは${MAX_COUNT}件までです`}
      selectedInfo={(procedureStatus: ProcedureStatus) => ({
        name: procedureStatus.employee.display_full_name,
      })}
    >
      {({list, selected, th}: SelectListProps) => {
        const selectedCount = reduce((sum, value) => (value ? sum + 1 : sum), 0, selected)
        const officeName = list[0]?.item?.employee?.office?.name

        return (
          <div>
            <CsvListData />

            {runningRequest && isActedUser && (
              <PollingController status={runningRequest.status} startPolling={startPolling} stopPolling={stopPolling} />
            )}

            <div className="l-contents-wrap">
              <div className="u-pb10">
                <h1 className="m-title-main">手続きを選択してください</h1>
                {hasBranchOffice && <p className="m-title-main-name">適用事業所：{recordDisplay(officeName)}</p>}
                <p className="m-title-main-name">
                  {label}：{PROCEDURE_NAMES[procedure_kind]}
                </p>
              </div>

              {isEgovPrepare && prepareRequest && (
                <PrepareCsvProcedureAsyncStatus prepareCsvProcedureRequest={prepareRequest} />
              )}

              {validationErrors.length > 0 && <ValidationErrors validationErrors={validationErrors} />}

              <div className="l-wrap-xxl">
                <div className={styles.chooseEmployee}>
                  <div>
                    {!loading && list.length === 0 ? (
                      <p className={styles.searchResultErrorMessage}>手続きが見つかりませんでした</p>
                    ) : (
                      <p className={styles.employeeCount}>選択した従業員：{selectedCount}名</p>
                    )}
                  </div>

                  <div className={styles.searchButton}>
                    <Search placeholder="スタッフコード、氏名、メール" />
                  </div>
                </div>
                <div className="l-overflow-scroll">
                  <table className="m-table-list m-table-fixed">
                    <thead>
                      <tr>
                        {th}
                        <th className={styles.flagColumn}>フラグ</th>
                        <th className="m-table-list-date">開始日</th>
                        <th className="m-table-list-staffcode">スタッフコード</th>
                        <th className={styles.nameColumn}>氏名</th>
                        {hasBranchOffice && <th className={styles.officeColumn}>適用事業所</th>}
                        <th className={styles.procedureColumn}>手続き</th>
                        <th className={styles.statusColumn}>ステータス</th>
                        <OrderedTh
                          addclass="m-table-list-date"
                          text={dateNameMap[procedure_type] || '関連日'}
                          column="date_on"
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {list.map(({item: procedureStatus, td}) => (
                        <Row
                          procedureStatus={procedureStatus}
                          key={procedureStatus.id}
                          td={td}
                          hasBranchOffice={hasBranchOffice}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="m-pager-area">
                  <Pager totalResult={count} pageLinks={7} />
                  <PerPage />
                </div>

                <div className="u-ta-c u-mt30">
                  {isEgovPrepare && (
                    <EgovPrepareModal
                      selected={selected}
                      procedureKind={procedure_kind as egovProcedureKind}
                      setValidationErrors={setValidationErrors}
                      refetch={refetch}
                      isTaskRunning={!!runningRequest}
                      isActedUser={isActedUser}
                      officeIds={officeIds}
                    />
                  )}

                  {isExportCsv && (
                    <CsvDownloadModal
                      selected={selected}
                      procedureKind={procedure_kind}
                      procedureStatuses={procedureStatuses}
                      officeIds={officeIds}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }}
    </SelectList>
  )
}

const Row: FC<RowProps> = ({procedureStatus, td, hasBranchOffice}) => {
  const employee = procedureStatus.employee

  return (
    <tr className="table-hover">
      {td}
      <StarFlagColumn procedureStatus={procedureStatus} />
      <td>{recordDisplay(procedureStatus.created_at && moment(procedureStatus.created_at).format('YYYY/MM/DD'))}</td>
      <td>{recordDisplay(employee.staff_code)}</td>
      <td>{recordDisplay(employee.display_last_name && employee.display_full_name)}</td>
      {hasBranchOffice && <td>{recordDisplay(employee.office.name)}</td>}
      <td>{recordDisplay(procedureTypes[procedureStatus.procedure_type])}</td>
      <td>{statuses[procedureStatus.status]}</td>
      <td>{recordDisplay(procedureStatus.date_on && procedureStatus.date_on.replace(/-/g, '/'))}</td>
    </tr>
  )
}
